/**
 * Vuex module for managing analysis data with lazy loading
 * Handles metadata, gestures, transcription, landmarks (paginated), and beliefs
 * 
 * This module optimizes loading of large analysis data by:
 * - Loading data in separate requests
 * - Paginating landmarks by time windows (10s)
 * - Caching loaded data to avoid duplicate requests
 * - Prefetching next landmarks window during video playback
 */

import InterviewAnalysisDataService from '@/services/InterviewAnalysisDataService';
import { normalizeGestureDocumentNames } from '@/utils/gestureLabels';

const WINDOW_SIZE = 5; // seconds per landmarks window (0-5s, 5-10s, 10-15s, etc.)
const LANDMARKS_REQUEST_RETRY_DELAY_MS = 300;
const EMPTY_WINDOW_COOLDOWN_MS = 4000;
// Cap cached windows to avoid browser memory exhaustion (each window ~1-2MB of landmarks)
const MAX_CACHED_WINDOWS_SHORT_VIDEO = 48; // Short videos: keep up to 48 windows (240s)
const MAX_CACHED_WINDOWS_LONG_VIDEO = 24; // Long videos: keep 24 windows (120s), LRU eviction
const ACTIVE_RUN_SEGMENT = 'active';
const MANUAL_SOURCE_TYPES = new Set(['manual_analysis', 'manual']);

/**
 * Composite store key (M2 contract 9): '<sourceType>:<sourceId>:<runId|active>'.
 * Every cached slice (metadata, gestures, landmarks windows, seek signals, alerts) is keyed by it,
 * so two runs of the same video never share state.
 *
 * @param {'interview'|'manual_analysis'} sourceType
 * @param {number|string} sourceId
 * @param {string|null|undefined} runId omitted -> the active run (SQL pointer)
 */
export function analysisKey(sourceType, sourceId, runId = null) {
  const run = typeof runId === 'string' && runId.trim() ? runId.trim() : ACTIVE_RUN_SEGMENT;
  return `${sourceType}:${sourceId}:${run}`;
}

/**
 * Inverse of analysisKey. Plain numeric ids (legacy callers) are accepted and map to
 * { sourceType: null, sourceId: <number>, runId: null, composite: false }.
 */
export function parseAnalysisKey(key) {
  if (key === null || key === undefined || key === '') {
    return { sourceType: null, sourceId: null, runId: null, composite: false };
  }
  if (typeof key === 'number') {
    return { sourceType: null, sourceId: key, runId: null, composite: false };
  }
  const raw = String(key);
  const parts = raw.split(':');
  if (parts.length < 3 || !parts[0] || !parts[1]) {
    const n = Number(raw);
    return {
      sourceType: null,
      sourceId: Number.isFinite(n) && raw.trim() !== '' ? n : raw,
      runId: null,
      composite: false,
    };
  }
  const [sourceType, sourceIdRaw, ...runParts] = parts;
  const runSegment = runParts.join(':');
  const numericId = Number(sourceIdRaw);
  return {
    sourceType,
    sourceId: Number.isFinite(numericId) ? numericId : sourceIdRaw,
    runId: runSegment && runSegment !== ACTIVE_RUN_SEGMENT ? runSegment : null,
    composite: true,
  };
}

/**
 * What the services need for a store key: numeric source id, manual flag and optional run.
 * For composite keys the source type wins over the `isManual` argument; legacy numeric keys
 * keep the caller's `isManual`.
 */
export function resolveFetchTarget(key, isManual = false) {
  const parsed = parseAnalysisKey(key);
  if (!parsed.composite) {
    return { sourceId: parsed.sourceId, isManual: !!isManual, runId: null };
  }
  return {
    sourceId: parsed.sourceId,
    isManual: MANUAL_SOURCE_TYPES.has(parsed.sourceType),
    runId: parsed.runId,
  };
}

function formatTimeFromMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return '00:00:000';
  const totalSec = n / 1000;
  const mins = Math.floor(totalSec / 60);
  const secs = Math.floor(totalSec % 60);
  const millis = Math.floor(n % 1000);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(millis).padStart(3, '0')}`;
}

function buildEpisodeStartTimeMsMap(episodes) {
  const map = new Map();
  if (!Array.isArray(episodes)) return map;
  for (const ep of episodes) {
    if (!ep || typeof ep !== 'object') continue;
    const id = ep.episode_id ?? ep.episodeId;
    const start = ep.start_time_ms ?? ep.startTimeMs;
    if (id == null || start == null) continue;
    const n = Number(start);
    if (Number.isFinite(n)) map.set(String(id), n);
  }
  return map;
}

/**
 * Start time in ms for a gesture_results alert (timestamp_ms may be missing in some payloads).
 */
function resolveGestureAlertTimestampMs(alert, episodeStartById) {
  if (!alert || typeof alert !== 'object') return 0;
  const coerce = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  for (const key of ['timestamp_ms', 'timestampMs', 'start_time_ms', 'startTimeMs', 'time_ms', 'timeMs']) {
    const n = coerce(alert[key]);
    if (n !== null) return n;
  }
  const eid = alert.episode_id ?? alert.episodeId;
  if (eid != null && episodeStartById && episodeStartById.has(String(eid))) {
    return episodeStartById.get(String(eid));
  }
  return 0;
}

function normalizeGestureClassification(rawClassification, magnitude = null) {
  const value = String(rawClassification || '').trim().toLowerCase();
  if (value === 'gesture' || value === 'microgesture' || value === 'alert' || value === 'red') {
    return 'gesture';
  }
  if (value === 'sustained' || value === 'warning' || value === 'orange') {
    return 'sustained';
  }
  const numericMagnitude = Number(magnitude);
  if (Number.isFinite(numericMagnitude)) {
    return numericMagnitude >= 80 ? 'gesture' : 'sustained';
  }
  return 'sustained';
}

const state = {
  // Core data (loaded once per analysis)
  metadata: {},        // { [analysisId]: { videoUrl, interviewInfo, status, config, ... } }
  gestures: {},        // { [analysisId]: { episodes, alerts, baseline, ... } from gesture_results }
  transcription: {},   // { [analysisId]: transcription[] }
  beliefs: {},         // { [analysisId]: beliefsAnalysis }
  // Run block from the metadata endpoint (M2 contract 4); null for legacy docs without ledger
  runInfo: {},         // { [analysisKey]: metadata.run | null }
  
  // Landmarks (paginated by time windows)
  landmarksWindows: {}, // { [analysisId]: { [windowIndex]: landmarksData[] } }
  // LRU order of loaded landmarks windows (oldest -> newest)
  landmarksWindowAccessOrder: {}, // { [analysisId]: number[] }
  // Windows recently returned empty, with cooldown timestamp to avoid hot loops
  emptyLandmarksWindows: {}, // { [analysisId]: { [windowIndex]: timestampMs } }
  
  // Loading states
  loading: {},         // { [analysisId]: { metadata: bool, gestures: bool, landmarks: bool, ... } }
  
  // Track which windows are currently being fetched (prevent duplicate requests)
  // STRICT: This Set is checked atomically before any fetch to prevent duplicates
  fetchingWindows: {}, // { [analysisId]: Set<windowIndex> }
  
  // Track active fetch Promises to reuse them (prevent duplicate HTTP requests)
  // STRICT: Multiple calls for same window will share the same Promise
  activeFetchPromises: {}, // { [analysisId]: { [windowIndex]: Promise } }
  
  // Track last prefetch time per window to prevent rapid duplicate prefetches
  lastPrefetchTime: {}, // { [analysisId]: { [windowIndex]: timestamp } }
  
  // Errors
  errors: {},          // { [analysisId]: { metadata: string|null, ... } }
  
  // Current analysis being viewed
  currentAnalysisId: null,
  
  // Whether current analysis is manual (stored to avoid passing through all components)
  isManualAnalysis: false,

  /**
   * Cross-tab video seek: Non Verbal tab owns the player; other tabs dispatch here.
   * Shape: { [analysisId]: { seconds: number, nonce: number } }
   */
  interviewVideoSeekSignals: {},
  
  // Window size constant (exposed for components)
  windowSize: WINDOW_SIZE,
};

const getters = {
  // ============================================================================
  // Check if data exists
  // ============================================================================
  
  hasMetadata: (state) => (analysisId) => !!state.metadata[analysisId],
  hasGestures: (state) => (analysisId) => !!state.gestures[analysisId],
  hasTranscription: (state) => (analysisId) => !!state.transcription[analysisId],
  hasBeliefs: (state) => (analysisId) => !!state.beliefs[analysisId],
  
  hasLandmarksWindow: (state) => (analysisId, windowIndex) => {
    return !!state.landmarksWindows[analysisId]?.[windowIndex];
  },
  isLandmarksWindowKnownEmpty: (state) => (analysisId, windowIndex) => {
    const markedAt = state.emptyLandmarksWindows[analysisId]?.[windowIndex];
    if (!Number.isFinite(markedAt)) return false;
    return Date.now() - markedAt < EMPTY_WINDOW_COOLDOWN_MS;
  },
  hasAnyLandmarksWindow: (state) => (analysisId) => {
    const windows = state.landmarksWindows[analysisId];
    return !!windows && Object.keys(windows).length > 0;
  },
  
  // ============================================================================
  // Get data
  // ============================================================================
  
  getMetadata: (state) => (analysisId) => state.metadata[analysisId] || null,
  getGestures: (state) => (analysisId) => state.gestures[analysisId] || null,
  /**
   * Table rows for the gesture tab: derived from alerts (new ai-service structure).
   * Each row: { time, is_speaking, alert, direction, head_pose, lips, brows } for same table UI.
   */
  getCoherenceAlerts: (state) => (analysisId) => {
    const g = state.gestures[analysisId];
    const alerts = g?.coherence_analysis?.coherence_alerts;
    return Array.isArray(alerts) ? alerts : [];
  },
  /**
   * Semaphore band thresholds from the analysis config snapshot (per-run), with defaults.
   */
  getCoherenceSemaphoreThresholds: (state) => (analysisId) => {
    const g = state.gestures[analysisId];
    const snap = g?.analysis_config_snapshot ?? g?.analysisConfigSnapshot;
    const engines = snap?.engines;
    const ver = engines?.coherence ?? {};
    const good = Number(ver.semaphoreThresholdGood);
    const warn = Number(ver.semaphoreThresholdWarn);
    return {
      good: Number.isFinite(good) ? good : 80,
      warn: Number.isFinite(warn) ? warn : 50,
    };
  },
  getCoherenceSemanticModes: (state) => (analysisId) => {
    const g = state.gestures[analysisId];
    const raw = g?.coherence_analysis?.semantic_modes;
    if (!raw || !raw.diarization) return null;
    const diarization = raw.diarization || {};
    const merged = Array.isArray(diarization.blocks_merged)
      ? diarization.blocks_merged
      : Array.isArray(diarization.blocks)
        ? diarization.blocks
        : [];
    const perAlert = Array.isArray(diarization.blocks_per_alert)
      ? diarization.blocks_per_alert
      : [];
    const idea = Array.isArray(diarization.blocks_idea)
      ? diarization.blocks_idea
      : [];
    const none = Array.isArray(diarization.blocks_none)
      ? diarization.blocks_none
      : [];
    const mergedParents = Array.isArray(diarization.blocks_merged_parents)
      ? diarization.blocks_merged_parents
      : [];
    const words = Array.isArray(diarization.words) ? diarization.words : [];
    return {
      diarization: {
        ...diarization,
        blocks: merged,
        blocks_merged: merged,
        blocks_merged_parents: mergedParents,
        blocks_per_alert: perAlert,
        blocks_idea: idea,
        blocks_none: none,
        words,
      },
    };
  },
  getGestureTableRows: (state) => (analysisId) => {
    const g = state.gestures[analysisId];
    if (!g || !Array.isArray(g.alerts) || g.alerts.length === 0) return [];
    const episodeStarts = buildEpisodeStartTimeMsMap(g.episodes);
    return g.alerts.map((a) => {
      const tsMs = resolveGestureAlertTimestampMs(a, episodeStarts);
      const durRaw = a.duration_ms ?? a.durationMs;
      const durationMs = durRaw != null ? Number(durRaw) : 0;
      return {
        time: formatTimeFromMs(tsMs),
        // Exact ms kept next to the mm:ss label: the feedback time coordinate needs it.
        timestamp_ms: Number.isFinite(Number(tsMs)) ? Number(tsMs) : null,
        is_speaking: false,
        alert: a.gesture_name || '-',
        direction: '-',
        head_pose: '-',
        lips: '-',
        brows: a.reason || '-',
        classification: normalizeGestureClassification(a.classification, a.magnitude),
        duration_seconds: Number.isFinite(durationMs) ? durationMs / 1000 : 0,
        // Stable join key for habituality lookups (same-ms same-name episodes
        // can otherwise collide in the time+name fallback index).
        episode_id: a.episode_id ?? a.episodeId ?? null,
      };
    });
  },
  getTranscription: (state) => (analysisId) => state.transcription[analysisId] || [],
  getBeliefs: (state) => (analysisId) => state.beliefs[analysisId] || null,

  // ============================================================================
  // Run info (metadata.run block, keyed by analysisKey)
  // ============================================================================

  getRunInfo: (state) => (analysisId) => state.runInfo[analysisId] || null,
  /** Run block of the analysis currently being viewed (null for legacy docs). */
  currentRun: (state) =>
    state.currentAnalysisId != null ? state.runInfo[state.currentAnalysisId] || null : null,
  /** Metadata loaded but no ledger run behind it: a legacy result doc. */
  isLegacyRun: (state) => (analysisId) => {
    if (!state.metadata[analysisId]) return false;
    const run = state.runInfo[analysisId];
    return !run || !run.run_id || String(run.run_id).startsWith('legacy-');
  },
  enginesExecuted: (state) => (analysisId) => {
    const run = state.runInfo[analysisId];
    return Array.isArray(run?.engines_executed) ? run.engines_executed : [];
  },
  /**
   * Engine selection of the run being viewed (M3 contract 3/8):
   * `{ requested, executed, skipped: [{name, reason}], partial, known }`. `known` is false for
   * legacy docs without the block (every engine is then assumed to have run).
   */
  getEnginesRun: (state) => (analysisId) => {
    const run = state.runInfo[analysisId] || null;
    const meta = state.metadata[analysisId] || null;
    const source = run && Array.isArray(run.engines_executed) ? run : meta;
    const requested = Array.isArray(source?.engines_requested) ? source.engines_requested : [];
    const executed = Array.isArray(source?.engines_executed) ? source.engines_executed : [];
    const skipped = Array.isArray(source?.engines_skipped)
      ? source.engines_skipped
          .map((item) =>
            typeof item === 'string'
              ? { name: item, reason: 'not_requested' }
              : item && typeof item === 'object' && item.name
                ? { name: item.name, reason: item.reason || 'not_requested' }
                : null
          )
          .filter(Boolean)
      : [];
    const known =
      Boolean(source) && (executed.length > 0 || skipped.length > 0 || requested.length > 0);
    return {
      requested,
      executed,
      skipped,
      partial: typeof source?.partial === 'boolean' ? source.partial : skipped.length > 0,
      known,
      status: run?.status || null,
      error: run?.error || null,
    };
  },
  /** Skip reason of an engine in the viewed run ('not_requested', 'no_list_component', ...) or null. */
  getEngineSkipReason: (state, getters) => (analysisId, engine) => {
    const info = getters.getEnginesRun(analysisId);
    if (!info.known) return null;
    const entry = info.skipped.find((item) => item.name === engine);
    if (entry) return entry.reason;
    // Engines absent from executed on a run with a known selection were not run.
    return info.executed.includes(engine) ? null : 'not_requested';
  },
  isEngineSkipped: (state, getters) => (analysisId, engine) =>
    getters.getEngineSkipReason(analysisId, engine) != null,
  /** False once retention purged the landmarks chunks of this run (metadata.landmarks_retained). */
  isLandmarksRetained: (state) => (analysisId) => {
    const meta = state.metadata[analysisId];
    if (!meta) return true;
    if (typeof meta.landmarks_retained === 'boolean') return meta.landmarks_retained;
    const run = state.runInfo[analysisId];
    return typeof run?.landmarks_retained === 'boolean' ? run.landmarks_retained : true;
  },
  
  /**
   * Get landmarks frame at specific time
   * Returns the closest frame to the given time, or null if not loaded
   */
  getLandmarksAtTime: (state) => (analysisId, timeSeconds) => {
    const windowIndex = Math.floor(timeSeconds / WINDOW_SIZE);
    const windowData = state.landmarksWindows[analysisId]?.[windowIndex];
    
    if (!windowData || windowData.length === 0) return null;
    
    // Find closest frame
    let closest = null;
    let minDiff = Infinity;
    
    for (const frame of windowData) {
      const diff = Math.abs(frame.time_seconds - timeSeconds);
      if (diff < minDiff) {
        minDiff = diff;
        closest = frame;
      }
      // Early exit if we've passed the target time
      if (frame.time_seconds > timeSeconds + 0.5) break;
    }
    
    return minDiff < 0.5 ? closest : null;
  },
  
  /**
   * Get all landmarks for a specific window
   */
  getLandmarksWindow: (state) => (analysisId, windowIndex) => {
    return state.landmarksWindows[analysisId]?.[windowIndex] || [];
  },
  
  // ============================================================================
  // Loading states
  // ============================================================================
  
  isLoading: (state) => (analysisId, type) => {
    return state.loading[analysisId]?.[type] || false;
  },
  
  isLoadingMetadata: (state) => (analysisId) => {
    return state.loading[analysisId]?.metadata || false;
  },
  
  isLoadingGestures: (state) => (analysisId) => {
    return state.loading[analysisId]?.gestures || false;
  },
  
  isLoadingLandmarks: (state) => (analysisId) => {
    return state.loading[analysisId]?.landmarks || false;
  },
  
  isLoadingBeliefs: (state) => (analysisId) => {
    return state.loading[analysisId]?.beliefs || false;
  },
  
  /**
   * Check if any initial data is still loading
   */
  isLoadingInitial: (state) => (analysisId) => {
    const loadingState = state.loading[analysisId];
    if (!loadingState) return false;
    return loadingState.metadata || loadingState.gestures || loadingState.transcription;
  },
  
  // ============================================================================
  // Errors
  // ============================================================================
  
  getError: (state) => (analysisId, type) => {
    return state.errors[analysisId]?.[type] || null;
  },
  
  hasError: (state) => (analysisId) => {
    const errors = state.errors[analysisId];
    if (!errors) return false;
    return Object.values(errors).some(e => e !== null);
  },
  
  // ============================================================================
  // Utilities
  // ============================================================================
  
  /**
   * Calculate window index from time in seconds
   */
  getWindowIndex: () => (timeSeconds) => {
    return Math.floor(timeSeconds / WINDOW_SIZE);
  },
  
  /**
   * Get the window size constant
   */
  getWindowSize: (state) => state.windowSize,
  
  /**
   * Get current analysis ID
   */
  getCurrentAnalysisId: (state) => state.currentAnalysisId,
  
  /**
   * Check if current analysis is manual
   */
  getIsManualAnalysis: (state) => state.isManualAnalysis,

  /**
   * Latest seek request for an interview analysis (consumers should watch and seek, not clear).
   */
  getInterviewVideoSeekSignal: (state) => (analysisId) =>
    state.interviewVideoSeekSignals[analysisId] || null,
};

const mutations = {
  SET_CURRENT_ANALYSIS(state, analysisId) {
    state.currentAnalysisId = analysisId;
  },
  
  SET_IS_MANUAL_ANALYSIS(state, isManual) {
    state.isManualAnalysis = isManual;
  },
  
  SET_METADATA(state, { analysisId, data }) {
    state.metadata = { ...state.metadata, [analysisId]: data };
    const run = data && typeof data === 'object' && data.run && typeof data.run === 'object'
      ? data.run
      : null;
    state.runInfo = { ...state.runInfo, [analysisId]: run };
  },

  SET_GESTURES(state, { analysisId, data }) {
    state.gestures = { ...state.gestures, [analysisId]: data };
  },
  
  SET_TRANSCRIPTION(state, { analysisId, data }) {
    state.transcription = { ...state.transcription, [analysisId]: data };
  },
  
  SET_BELIEFS(state, { analysisId, data }) {
    state.beliefs = { ...state.beliefs, [analysisId]: data };
  },
  
  SET_LANDMARKS_WINDOW(state, { analysisId, windowIndex, data }) {
    if (!state.landmarksWindows[analysisId]) {
      state.landmarksWindows = { 
        ...state.landmarksWindows, 
        [analysisId]: {} 
      };
    }

    if (!state.landmarksWindowAccessOrder[analysisId]) {
      state.landmarksWindowAccessOrder[analysisId] = [];
    }
    if (!state.emptyLandmarksWindows[analysisId]) {
      state.emptyLandmarksWindows[analysisId] = {};
    }

    const windows = state.landmarksWindows[analysisId];

    // Clear "known empty" flag when we receive data for this window.
    delete state.emptyLandmarksWindows[analysisId][windowIndex];

    state.landmarksWindows[analysisId] = {
      ...windows,
      [windowIndex]: data,
    };

    const order = state.landmarksWindowAccessOrder[analysisId];
    const withoutCurrent = order.filter((idx) => idx !== windowIndex);
    withoutCurrent.push(windowIndex);
    state.landmarksWindowAccessOrder[analysisId] = withoutCurrent;

    // Get video duration from metadata to determine cache strategy.
    const metadata = state.metadata[analysisId];
    const videoDuration = metadata?.interviewInfo?.duration || metadata?.duration || 0;
    const totalWindows = videoDuration > 0 ? Math.ceil(videoDuration / WINDOW_SIZE) : 0;
    const maxCachedWindows =
      totalWindows > 0 && totalWindows <= MAX_CACHED_WINDOWS_SHORT_VIDEO
        ? totalWindows
        : totalWindows === 0
          ? MAX_CACHED_WINDOWS_SHORT_VIDEO
          : MAX_CACHED_WINDOWS_LONG_VIDEO;

    // Strict LRU eviction: remove oldest loaded windows when limit is exceeded.
    while (state.landmarksWindowAccessOrder[analysisId].length > maxCachedWindows) {
      const evictedWindow = state.landmarksWindowAccessOrder[analysisId].shift();
      if (!Number.isFinite(evictedWindow)) break;
      if (evictedWindow === windowIndex) {
        state.landmarksWindowAccessOrder[analysisId].push(evictedWindow);
        break;
      }
      delete state.landmarksWindows[analysisId][evictedWindow];
      if (state.emptyLandmarksWindows[analysisId]) {
        delete state.emptyLandmarksWindows[analysisId][evictedWindow];
      }
    }
  },

  TOUCH_LANDMARKS_WINDOW(state, { analysisId, windowIndex }) {
    if (!state.landmarksWindowAccessOrder[analysisId]) {
      state.landmarksWindowAccessOrder[analysisId] = [];
    }
    const order = state.landmarksWindowAccessOrder[analysisId];
    const withoutCurrent = order.filter((idx) => idx !== windowIndex);
    withoutCurrent.push(windowIndex);
    state.landmarksWindowAccessOrder[analysisId] = withoutCurrent;
  },

  SET_EMPTY_LANDMARKS_WINDOW(state, { analysisId, windowIndex }) {
    if (!state.emptyLandmarksWindows[analysisId]) {
      state.emptyLandmarksWindows[analysisId] = {};
    }
    state.emptyLandmarksWindows[analysisId][windowIndex] = Date.now();
  },

  CLEAR_EMPTY_LANDMARKS_WINDOW(state, { analysisId, windowIndex }) {
    if (!state.emptyLandmarksWindows[analysisId]) return;
    delete state.emptyLandmarksWindows[analysisId][windowIndex];
  },
  
  SET_LOADING(state, { analysisId, type, isLoading }) {
    if (!state.loading[analysisId]) {
      state.loading = { ...state.loading, [analysisId]: {} };
    }
    state.loading[analysisId] = {
      ...state.loading[analysisId],
      [type]: isLoading,
    };
  },
  
  SET_ERROR(state, { analysisId, type, error }) {
    if (!state.errors[analysisId]) {
      state.errors = { ...state.errors, [analysisId]: {} };
    }
    state.errors[analysisId] = {
      ...state.errors[analysisId],
      [type]: error,
    };
  },
  
  REQUEST_INTERVIEW_VIDEO_SEEK(state, { analysisId, seconds }) {
    if (analysisId === null || analysisId === undefined) return;
    const s = Number(seconds);
    if (!Number.isFinite(s)) return;
    state.interviewVideoSeekSignals = {
      ...state.interviewVideoSeekSignals,
      [analysisId]: { seconds: s, nonce: Date.now() },
    };
  },

  CLEAR_ANALYSIS(state, analysisId) {
    delete state.metadata[analysisId];
    delete state.gestures[analysisId];
    delete state.transcription[analysisId];
    delete state.beliefs[analysisId];
    delete state.runInfo[analysisId];
    delete state.landmarksWindows[analysisId];
    delete state.loading[analysisId];
    delete state.errors[analysisId];
    // STRICT: Clear fetching state to prevent stuck requests
    delete state.fetchingWindows[analysisId];
    delete state.activeFetchPromises[analysisId];
    delete state.lastPrefetchTime[analysisId];
    delete state.landmarksWindowAccessOrder[analysisId];
    delete state.emptyLandmarksWindows[analysisId];
    delete state.interviewVideoSeekSignals[analysisId];

    if (state.currentAnalysisId === analysisId) {
      state.currentAnalysisId = null;
      state.isManualAnalysis = false;
    }
  },
  
  CLEAR_ALL(state) {
    state.metadata = {};
    state.gestures = {};
    state.transcription = {};
    state.beliefs = {};
    state.runInfo = {};
    state.landmarksWindows = {};
    state.loading = {};
    state.errors = {};
    // STRICT: Clear all fetching state
    state.fetchingWindows = {};
    state.activeFetchPromises = {};
    state.lastPrefetchTime = {};
    state.landmarksWindowAccessOrder = {};
    state.emptyLandmarksWindows = {};
    state.currentAnalysisId = null;
    state.isManualAnalysis = false;
    state.interviewVideoSeekSignals = {};
  },
};

const actions = {
  /**
   * Ask the interview video player (Non Verbal tab) to seek to seconds.
   */
  requestInterviewVideoSeek({ commit }, { analysisId, seconds }) {
    commit('REQUEST_INTERVIEW_VIDEO_SEEK', { analysisId, seconds });
  },

  // ============================================================================
  // Fetch individual data types
  // ============================================================================
  
  /**
   * Fetch metadata (if not cached)
   * @param {Object} payload - { analysisId: number|string (analysisKey), isManual: boolean, force: boolean }
   */
  async fetchMetadata({ commit, getters }, { analysisId, isManual = false, force = false }) {
    if (getters.hasMetadata(analysisId) && !force) {
      return;
    }
    const target = resolveFetchTarget(analysisId, isManual);
    
    commit('SET_LOADING', { analysisId, type: 'metadata', isLoading: true });
    commit('SET_ERROR', { analysisId, type: 'metadata', error: null });
    
    try {
      const response = await InterviewAnalysisDataService.getMetadata(
        target.sourceId,
        target.isManual,
        target.runId
      );
      commit('SET_METADATA', { analysisId, data: response.data.data });
    } catch (error) {
      console.error('[analysisData] Error fetching metadata:', error);
      commit('SET_ERROR', { analysisId, type: 'metadata', error: error.message });
      throw error;
    } finally {
      commit('SET_LOADING', { analysisId, type: 'metadata', isLoading: false });
    }
  },
  
  /**
   * Fetch gestures (if not cached). Source: new ai-service only (gesture_results).
   * @param {Object} payload - { analysisId: number, isManual: boolean }
   */
  async fetchGestures({ commit, dispatch, getters }, { analysisId, isManual = false }) {
    if (getters.hasGestures(analysisId)) {
      return;
    }
    const target = resolveFetchTarget(analysisId, isManual);
    
    commit('SET_LOADING', { analysisId, type: 'gestures', isLoading: true });
    commit('SET_ERROR', { analysisId, type: 'gestures', error: null });
    
    try {
      const response = await InterviewAnalysisDataService.getGestures(
        target.sourceId,
        target.isManual,
        target.runId
      );
      // Single ingestion point for stored gesture ids: Node serves the document
      // verbatim, so pre-rename documents carry legacy names here.
      const data = normalizeGestureDocumentNames(response.data.data || {});
      const payload = {
        episodes: data.episodes || [],
        alerts: data.alerts || [],
        baseline: data.baseline || {},
        gesture_data: data.gesture_data,
        audio_data: data.audio_data,
        landmarks_config: data.landmarks_config ?? null,
        landmarks_data: data.landmarks_data || [],
        coherence_analysis: data.coherence_analysis || null,
        analysis_config_snapshot:
          data.analysis_config_snapshot ?? data.analysisConfigSnapshot ?? null,
      };
      commit('SET_GESTURES', { analysisId, data: payload });
      const alerts = payload.alerts;
      if (alerts.length > 0) {
        const episodeStarts = buildEpisodeStartTimeMsMap(payload.episodes);
        const filteredAlerts = alerts.map((a) => {
          const tsMs = resolveGestureAlertTimestampMs(a, episodeStarts);
          return {
            time: formatTimeFromMs(tsMs),
            // Exact ms kept next to the mm:ss label: the feedback time coordinate needs it.
            timestamp_ms: Number.isFinite(Number(tsMs)) ? Number(tsMs) : null,
            gesture_type: normalizeGestureClassification(a.classification, a.magnitude),
            gesture_name: a.gesture_name || '-',
            magnitude: Number.isFinite(Number(a.magnitude))
              ? Number(a.magnitude)
              : (normalizeGestureClassification(a.classification, a.magnitude) === 'gesture' ? 80 : 50),
            // Propagated from backend so the timeline filters and tooltips have real data:
            reason: a.reason || null,
            duration_ms: Number.isFinite(Number(a.duration_ms)) ? Number(a.duration_ms) : null,
            gesture_kind: a.gesture_kind || 'spontaneous',
            is_speaking: typeof a.is_subject_speaking === 'boolean' ? a.is_subject_speaking : null,
            explain: a.explain || null,
            episode_id: a.episode_id || null,
          };
        });
        await dispatch('gestureAlerts/setFilteredAlerts', { alerts: filteredAlerts, analysisId }, { root: true });
      }
    } catch (error) {
      console.error('[analysisData] Error fetching gestures:', error);
      commit('SET_ERROR', { analysisId, type: 'gestures', error: error.message });
      throw error;
    } finally {
      commit('SET_LOADING', { analysisId, type: 'gestures', isLoading: false });
    }
  },
  
  /**
   * Fetch transcription (if not cached)
   * @param {Object} payload - { analysisId: number, isManual: boolean }
   */
  async fetchTranscription({ commit, getters }, { analysisId, isManual = false }) {
    if (getters.hasTranscription(analysisId)) {
      return;
    }
    const target = resolveFetchTarget(analysisId, isManual);
    
    commit('SET_LOADING', { analysisId, type: 'transcription', isLoading: true });
    commit('SET_ERROR', { analysisId, type: 'transcription', error: null });
    
    try {
      const response = await InterviewAnalysisDataService.getTranscription(
        target.sourceId,
        target.isManual,
        target.runId
      );
      const transcriptionData = response.data.data?.transcription || [];
      commit('SET_TRANSCRIPTION', { analysisId, data: transcriptionData });
    } catch (error) {
      console.error('[analysisData] Error fetching transcription:', error);
      commit('SET_ERROR', { analysisId, type: 'transcription', error: error.message });
      throw error;
    } finally {
      commit('SET_LOADING', { analysisId, type: 'transcription', isLoading: false });
    }
  },
  
  /**
   * Fetch beliefs (lazy - only when needed)
   * @param {Object} payload - { analysisId: number, isManual: boolean }
   */
  async fetchBeliefs({ commit, getters }, { analysisId, isManual = false }) {
    if (getters.hasBeliefs(analysisId)) {
      return;
    }
    const target = resolveFetchTarget(analysisId, isManual);
    
    commit('SET_LOADING', { analysisId, type: 'beliefs', isLoading: true });
    commit('SET_ERROR', { analysisId, type: 'beliefs', error: null });
    
    try {
      const response = await InterviewAnalysisDataService.getBeliefs(
        target.sourceId,
        target.isManual,
        target.runId
      );
      const beliefsData = response.data.data || null;
      commit('SET_BELIEFS', { analysisId, data: beliefsData });
    } catch (error) {
      console.error('[analysisData] Error fetching beliefs:', error);
      commit('SET_ERROR', { analysisId, type: 'beliefs', error: error.message });
      throw error;
    } finally {
      commit('SET_LOADING', { analysisId, type: 'beliefs', isLoading: false });
    }
  },
  
  /**
   * Fetch landmarks window (paginated)
   * @param {Object} payload - { analysisId: number, windowIndex: number, isManual: boolean }
   * STRICT: Returns existing Promise if already fetching to prevent duplicate HTTP requests
   */
  async fetchLandmarksWindow({ commit, getters, state }, { analysisId, windowIndex, isManual = false }) {
    // Landmarks are paginated by time windows; this function fetches one window and caches it in Vuex.
    // STRICT: Initialize fetching set and promises map first
    if (!state.fetchingWindows[analysisId]) {
      state.fetchingWindows[analysisId] = new Set();
    }
    if (!state.activeFetchPromises[analysisId]) {
      state.activeFetchPromises[analysisId] = {};
    }
    
    // CRITICAL: Check if already cached (early return) - THIS PREVENTS 304 LOOPS
    // If we already have it in Vuex, we NEVER make an HTTP request
    const isCached = getters.hasLandmarksWindow(analysisId, windowIndex);
    if (isCached) {
      commit('TOUCH_LANDMARKS_WINDOW', { analysisId, windowIndex });
      return Promise.resolve();
    }

    // If this window is already known to be empty, skip network to avoid loops.
    if (getters.isLandmarksWindowKnownEmpty(analysisId, windowIndex)) {
      return Promise.resolve();
    }
    
    // STRICT: Check if there's already an active Promise for this window
    // If yes, return the existing Promise instead of creating a new HTTP request
    const existingPromise = state.activeFetchPromises[analysisId][windowIndex];
    if (existingPromise) {
      return existingPromise;
    }
    
    // STRICT: Check if already being fetched (prevent duplicate requests)
    if (state.fetchingWindows[analysisId].has(windowIndex)) {
      return state.activeFetchPromises[analysisId][windowIndex] || Promise.resolve();
    }
    
    // STRICT: Mark as fetching IMMEDIATELY (before any async operation)
    state.fetchingWindows[analysisId].add(windowIndex);
    
    commit('SET_LOADING', { analysisId, type: 'landmarks', isLoading: true });
    commit('SET_ERROR', { analysisId, type: 'landmarks', error: null });
    
    const target = resolveFetchTarget(analysisId, isManual);

    // Create the fetch Promise and store it immediately
    const fetchPromise = (async () => {
      try {
        const startTime = windowIndex * WINDOW_SIZE;
        const endTime = startTime + WINDOW_SIZE;

        const getResponseWithRetry = async () => {
          try {
            return await InterviewAnalysisDataService.getLandmarks(
              target.sourceId,
              startTime,
              endTime,
              target.isManual,
              false,
              target.runId
            );
          } catch (initialError) {
            await new Promise((resolve) => setTimeout(resolve, LANDMARKS_REQUEST_RETRY_DELAY_MS));
            return InterviewAnalysisDataService.getLandmarks(
              target.sourceId,
              startTime,
              endTime,
              target.isManual,
              true,
              target.runId
            );
          }
        };

        const response = await getResponseWithRetry();
        
        // Keep this log for debugging landmarks loading issues
        console.log('[analysisData] Raw response from getLandmarks:', {
          status: response.status,
          hasData: !!response.data,
          hasDataData: !!(response.data && response.data.data),
          responseStructure: response.data ? Object.keys(response.data) : [],
          landmarksDataLength: response.data?.data?.landmarksData?.length || 0,
          sampleFrame: response.data?.data?.landmarksData?.[0] || null,
        });
        
        // Extract landmarks data from response
        const landmarksData = response.data?.data?.landmarksData || response.data?.landmarksData || [];
        
        if (landmarksData.length === 0) {
          commit('SET_EMPTY_LANDMARKS_WINDOW', { analysisId, windowIndex });
          return;
        }

        commit('CLEAR_EMPTY_LANDMARKS_WINDOW', { analysisId, windowIndex });
        
        commit('SET_LANDMARKS_WINDOW', { 
          analysisId, 
          windowIndex, 
          data: landmarksData 
        });
      } catch (error) {
        console.error('[analysisData] Error fetching landmarks window:', error);
        commit('SET_ERROR', { analysisId, type: 'landmarks', error: error.message });
        throw error;
      } finally {
        // STRICT: Always remove from fetching set and clear Promise
        if (state.fetchingWindows[analysisId]) {
          state.fetchingWindows[analysisId].delete(windowIndex);
        }
        if (state.activeFetchPromises[analysisId]) {
          delete state.activeFetchPromises[analysisId][windowIndex];
        }
        commit('SET_LOADING', { analysisId, type: 'landmarks', isLoading: false });
      }
    })();
    
    // Store the Promise immediately so concurrent calls can reuse it
    state.activeFetchPromises[analysisId][windowIndex] = fetchPromise;
    
    return fetchPromise;
  },
  
  // ============================================================================
  // Composite actions
  // ============================================================================
  
  /**
   * Ensure landmarks at time (for seek/playback)
   * Returns the landmarks frame at that time, loading if necessary
   * CRITICAL: Multiple concurrent calls for same window will share the same Promise
   * This prevents duplicate HTTP requests including 304 loops
   */
  async ensureLandmarksAtTime(
    { dispatch, getters, state, commit },
    { analysisId, timeSeconds, isManual = null }
  ) {
    const windowIndex = getters.getWindowIndex(timeSeconds);
    
    // Use explicit isManual if provided, otherwise fallback to state
    const isManualValue = isManual !== null ? isManual : state.isManualAnalysis;
    
    // CRITICAL: Check if already cached FIRST - this is the most important check
    if (getters.hasLandmarksWindow(analysisId, windowIndex)) {
      commit('TOUCH_LANDMARKS_WINDOW', { analysisId, windowIndex });
      return getters.getLandmarksAtTime(analysisId, timeSeconds);
    }

    if (getters.isLandmarksWindowKnownEmpty(analysisId, windowIndex)) {
      return null;
    }
    
    // Initialize promises map if needed
    if (!state.activeFetchPromises[analysisId]) {
      state.activeFetchPromises[analysisId] = {};
    }
    
    // Check if there's already an active Promise for this window
    const existingPromise = state.activeFetchPromises[analysisId][windowIndex];
    if (existingPromise) {
      try {
        await existingPromise;
        if (getters.hasLandmarksWindow(analysisId, windowIndex)) {
          commit('TOUCH_LANDMARKS_WINDOW', { analysisId, windowIndex });
          return getters.getLandmarksAtTime(analysisId, timeSeconds);
        }
        return null;
      } catch (error) {
        return null;
      }
    }
    
    // No existing Promise and not cached - create a new one
    try {
      const fetchPromise = dispatch('fetchLandmarksWindow', { 
        analysisId, 
        windowIndex, 
        isManual: isManualValue,
      });
      
      await fetchPromise;
      
      if (getters.hasLandmarksWindow(analysisId, windowIndex)) {
        commit('TOUCH_LANDMARKS_WINDOW', { analysisId, windowIndex });
        return getters.getLandmarksAtTime(analysisId, timeSeconds);
      }
      
      return null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Ensure a target window is loaded and warm adjacent windows for smooth seeking.
   * Virtual-scroll-like behavior: cache neighborhood and reuse in-memory windows.
   */
  async warmLandmarksAroundTime(
    { dispatch, getters, state },
    { analysisId, timeSeconds, isManual = null, radius = 1 }
  ) {
    const centerWindow = getters.getWindowIndex(timeSeconds);
    const safeRadius = Math.max(0, Math.min(3, Number(radius) || 0));
    const isManualValue = isManual !== null ? isManual : state.isManualAnalysis;
    const targets = [];

    for (let offset = -safeRadius; offset <= safeRadius; offset += 1) {
      const target = centerWindow + offset;
      if (target < 0) continue;
      targets.push(target);
    }

    await Promise.allSettled(
      targets.map((windowIndex) => {
        if (getters.hasLandmarksWindow(analysisId, windowIndex)) return Promise.resolve();
        if (getters.isLandmarksWindowKnownEmpty(analysisId, windowIndex)) return Promise.resolve();
        return dispatch('fetchLandmarksWindow', {
          analysisId,
          windowIndex,
          isManual: isManualValue,
        });
      })
    );

    return getters.getLandmarksAtTime(analysisId, timeSeconds);
  },
  
  /**
   * Prefetch next windows (for smooth playback)
   * Call this during video playback to preload upcoming windows
   * 
   * CONTROLLED PREFETCHING (max 2 simultaneous requests):
   * - Only prefetch if less than 2 requests are in flight
   * - With 5s windows, we prefetch earlier in the window
   * - Prefetch window + 1 at 20% (1s into 5s window)
   * - Prefetch window + 2 at 60% (3s into 5s window)
   * 
   * This prevents server overload while still preloading ahead.
   */
  prefetchNextWindow({ dispatch, getters, state }, { analysisId, currentTime }) {
    // Initialize state containers
    if (!state.fetchingWindows[analysisId]) {
      state.fetchingWindows[analysisId] = new Set();
    }
    if (!state.activeFetchPromises[analysisId]) {
      state.activeFetchPromises[analysisId] = {};
    }
    if (!state.lastPrefetchTime[analysisId]) {
      state.lastPrefetchTime[analysisId] = {};
    }
    
    const currentWindow = Math.floor(currentTime / WINDOW_SIZE);
    const positionInWindow = currentTime % WINDOW_SIZE;
    const now = Date.now();
    const PREFETCH_DEBOUNCE_MS = 1500; // Don't prefetch same window more than once per 1.5 seconds
    const MAX_SIMULTANEOUS_FETCHES = 2; // Limit concurrent requests to prevent server overload
    
    // Check how many fetches are currently in progress
    const activeFetchCount = state.fetchingWindows[analysisId]?.size || 0;
    
    // Don't start new prefetches if we already have too many in flight
    if (activeFetchCount >= MAX_SIMULTANEOUS_FETCHES) {
      return;
    }
    
    /**
     * Helper function to prefetch a specific window (returns true if started)
     */
    const tryPrefetchWindow = (windowOffset, triggerPercent) => {
      // Check again if we can start another fetch
      const currentActiveFetches = state.fetchingWindows[analysisId]?.size || 0;
      if (currentActiveFetches >= MAX_SIMULTANEOUS_FETCHES) {
        return false;
      }
      
      if (positionInWindow > WINDOW_SIZE * triggerPercent) {
        const targetWindow = currentWindow + windowOffset;
        const lastPrefetch = state.lastPrefetchTime[analysisId][targetWindow] || 0;
        const timeSinceLastPrefetch = now - lastPrefetch;
        
        const isCached = getters.hasLandmarksWindow(analysisId, targetWindow);
        const isFetching = state.fetchingWindows[analysisId]?.has(targetWindow);
        const isKnownEmpty = getters.isLandmarksWindowKnownEmpty(analysisId, targetWindow);
        const isDebounced = timeSinceLastPrefetch < PREFETCH_DEBOUNCE_MS;
        
        if (!isCached && !isFetching && !isKnownEmpty && !isDebounced) {
          state.lastPrefetchTime[analysisId][targetWindow] = now;
          dispatch('fetchLandmarksWindow', { 
            analysisId, 
            windowIndex: targetWindow, 
            isManual: state.isManualAnalysis,
          }).catch(() => {});
          return true;
        }
      }
      return false;
    };
    
    // Prefetch 2 windows ahead with staggered triggers (controlled)
    // With 5s windows: trigger at 1s (20%) and 3s (60%) into current window
    tryPrefetchWindow(1, 0.20); // window + 1 at 20% (1s into 5s window)
    tryPrefetchWindow(2, 0.60); // window + 2 at 60% (3s into 5s window)
  },
  
  /**
   * Load initial data (metadata + gestures + transcription + first landmarks window)
   * This is called when entering the analysis view
   * @param {Object} payload - { analysisId: number, isManual: boolean }
   */
  async fetchInitialData({ dispatch, commit, getters }, { analysisId, isManual = false }) {
    const target = resolveFetchTarget(analysisId, isManual);
    commit('SET_CURRENT_ANALYSIS', analysisId);
    commit('SET_IS_MANUAL_ANALYSIS', target.isManual);

    const results = await Promise.allSettled([
      dispatch('fetchMetadata', { analysisId, isManual }),
      dispatch('fetchGestures', { analysisId, isManual }),
      dispatch('fetchTranscription', { analysisId, isManual }),
      dispatch('fetchLandmarksWindow', { analysisId, windowIndex: 0, isManual }),
    ]);

    if (!getters.hasMetadata(analysisId)) {
      const metaRejection = results[0];
      const err = metaRejection?.reason || new Error('Could not load analysis metadata');
      throw err;
    }
  },
  
  /**
   * Re-fetch the metadata (and its run block) for a key, e.g. after promote / pin / unpin.
   * Gestures, transcription and landmarks are immutable per run and stay cached.
   */
  async refreshMetadata({ dispatch }, { analysisId, isManual = false }) {
    await dispatch('fetchMetadata', { analysisId, isManual, force: true });
  },

  /**
   * Clear cache for a specific analysis
   */
  clearAnalysis({ commit, dispatch }, analysisId) {
    commit('CLEAR_ANALYSIS', analysisId);
    dispatch('gestureAlerts/clearAlerts', null, { root: true });
  },
  
  /**
   * Clear all cached data
   */
  clearAll({ commit }) {
    commit('CLEAR_ALL');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
